import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from './queryKeys'
import {
  fetchReportData,
  fetchWorkspaceInit,
  fetchWorkspaceInsight,
  fetchWorkspaceGroupInsight,
  sendDeepDiveMessage,
  fetchDrilldownData,
  fetchDrilldownDimensions,
  fetchWorkspaceDimensions,
  fetchPARecipients,
  fetchCommunicateDraft,
  sendCommunication,
  rewriteMessage,
  fetchNotes,
  addNote,
  updateNote,
  deleteNote,
  fetchHistory,
  fetchActivityDetail,
} from './api'
import type {
  PracticeAgentSendDeepDiveMessagePayload,
  PracticeAgentCommunicatePayload,
  PracticeAgentDraftPayload,
  PracticeAgentRewritePayload,
  PracticeAgentAddNotePayload,
  PracticeAgentUpdateNotePayload,
  PracticeAgentHistoryFilters,
} from './types'

const STALE_TIME = 5 * 60 * 1000 // 5 minutes

// --- Workspace ---

export function usePracticeAgentReportData(
  clientId: string,
  reportBuilderId: string | undefined,
  filters: Record<string, string> = {},
  reportBuilderGroupId?: string,
  options?: { enabled?: boolean },
  page = 1,
  sortColumn?: string,
  sortDirection?: 'asc' | 'desc',
  groupByDimension?: string,
) {
  return useQuery({
    queryKey: queryKeys.workspaceData(clientId, reportBuilderId ?? '', reportBuilderGroupId ?? '', filters, page, sortColumn ?? '', sortDirection ?? 'desc', groupByDimension ?? ''),
    queryFn: () => fetchReportData(reportBuilderId ?? '', clientId, filters, reportBuilderGroupId, page, sortColumn, sortDirection, undefined, groupByDimension),
    enabled: (options?.enabled ?? true) && !!reportBuilderId,
    staleTime: STALE_TIME,
    gcTime: 60 * 1000,
  })
}

export function usePracticeAgentWorkspaceInsight(
  clientId: string,
  reportBuilderId: string | undefined,
  filters: Record<string, string> = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.workspaceInsight(clientId, reportBuilderId ?? '', filters),
    queryFn: () => fetchWorkspaceInsight({ clientId, reportBuilderId: reportBuilderId ?? '', filters }),
    enabled: (options?.enabled ?? true) && !!clientId && !!reportBuilderId,
    staleTime: STALE_TIME,
    gcTime: 60 * 1000,
  })
}

export function usePracticeAgentWorkspaceGroupInsight(
  clientId: string,
  reportBuilderGroupId: string | undefined,
  filters: Record<string, string> = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.workspaceGroupInsight(clientId, reportBuilderGroupId ?? '', filters),
    queryFn: () => fetchWorkspaceGroupInsight({ clientId, reportBuilderGroupId: reportBuilderGroupId ?? '', filters }),
    enabled: (options?.enabled ?? true) && !!clientId && !!reportBuilderGroupId,
    staleTime: STALE_TIME,
    gcTime: 60 * 1000,
  })
}

export function usePracticeAgentWorkspaceInit(
  clientId: string,
  reportBuilderId: string | undefined,
  reportBuilderGroupId: string | undefined,
  filters: Record<string, string> = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.workspaceInit(clientId, reportBuilderId ?? '', reportBuilderGroupId ?? '', filters),
    queryFn: () => fetchWorkspaceInit({ clientId, reportBuilderId, reportBuilderGroupId, filters }),
    enabled: (options?.enabled ?? true) && !!clientId && (!!reportBuilderId || !!reportBuilderGroupId),
    staleTime: STALE_TIME,
    gcTime: 60 * 1000,
  })
}

// --- Deep Dive ---

export function usePracticeAgentSendDeepDiveMessageMutation() {
  return useMutation({
    mutationFn: (payload: PracticeAgentSendDeepDiveMessagePayload) => sendDeepDiveMessage(payload),
  })
}


export function useDrilldownDimensions(clientId: string, platformType: number | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['practiceAgent', 'drilldownDimensions', clientId, platformType] as const,
    queryFn: () => fetchDrilldownDimensions(clientId, platformType ?? 0),
    enabled: !!clientId && platformType != null && (options?.enabled ?? true),
  })
}

export function useWorkspaceDimensions(
  clientId: string,
  reportBuilderId: string | undefined,
  reportBuilderGroupId: string | undefined,
) {
  return useQuery({
    queryKey: ['practiceAgent', 'workspaceDimensions', clientId, reportBuilderId ?? '', reportBuilderGroupId ?? ''] as const,
    queryFn: () => fetchWorkspaceDimensions(clientId, reportBuilderId, reportBuilderGroupId),
    enabled: !!clientId && !!(reportBuilderId || reportBuilderGroupId),
    staleTime: STALE_TIME,
    gcTime: 10 * 60 * 1000,
  })
}

export function usePracticeAgentExecuteDrilldownMutation() {
  return useMutation({
    mutationFn: ({
      reportBuilderId,
      field,
      value,
      dimension,
      page = 1,
      reportBuilderGroupId,
    }: {
      reportBuilderId: string
      field: string
      value: string
      dimension: string
      page?: number
      reportBuilderGroupId?: string
    }) => fetchDrilldownData(reportBuilderId, field, value, dimension, page, reportBuilderGroupId),
  })
}

// --- Communicate ---

export function usePracticeAgentRecipients(clientId: string) {
  return useQuery({
    queryKey: queryKeys.paRecipients(clientId),
    queryFn: () => fetchPARecipients(clientId),
    enabled: !!clientId,
  })
}

export function usePracticeAgentFetchCommunicateDraftMutation() {
  return useMutation({
    mutationFn: (payload: PracticeAgentDraftPayload) => fetchCommunicateDraft(payload),
  })
}

export function usePracticeAgentSendCommunicationMutation() {
  return useMutation({
    mutationFn: (payload: PracticeAgentCommunicatePayload) => sendCommunication(payload),
  })
}

export function usePracticeAgentRewriteMessageMutation() {
  return useMutation({
    mutationFn: (payload: PracticeAgentRewritePayload) => rewriteMessage(payload),
  })
}

// --- Notes ---

export function usePracticeAgentNotes(
  clientId: string,
  reportBuilderId?: string | null,
  reportBuilderGroupId?: string | null,
) {
  return useQuery({
    queryKey: queryKeys.notes(clientId, reportBuilderId, reportBuilderGroupId),
    queryFn: () => fetchNotes({ reportBuilderId, reportBuilderGroupId, clientId }),
    enabled: !!(clientId && (reportBuilderId || reportBuilderGroupId)),
  })
}

export function usePracticeAgentAddNoteMutation(
  clientId: string,
  reportBuilderId?: string | null,
  reportBuilderGroupId?: string | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PracticeAgentAddNotePayload) => addNote(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notes(clientId, reportBuilderId, reportBuilderGroupId),
      })
    },
  })
}

export function usePracticeAgentUpdateNoteMutation(
  clientId: string,
  reportBuilderId?: string | null,
  reportBuilderGroupId?: string | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: PracticeAgentUpdateNotePayload) => updateNote(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notes(clientId, reportBuilderId, reportBuilderGroupId),
      })
    },
  })
}

export function usePracticeAgentDeleteNoteMutation(
  clientId: string,
  reportBuilderId?: string | null,
  reportBuilderGroupId?: string | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (noteId: string) => deleteNote(noteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notes(clientId, reportBuilderId, reportBuilderGroupId),
      })
    },
  })
}

// --- History ---

export function usePracticeAgentHistory(clientId: string, filters: PracticeAgentHistoryFilters = {}) {
  return useQuery({
    queryKey: queryKeys.paHistory(clientId, filters as Record<string, unknown>),
    queryFn: () => fetchHistory(clientId, filters),
    staleTime: 0,
    gcTime: 30 * 1000,
  })
}

export function usePracticeAgentActivityDetail(activityId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.activityDetail(activityId ?? ''),
    queryFn: () => fetchActivityDetail(activityId!),
    enabled: !!activityId,
  })
}
